import { Popover } from '@headlessui/react';
import { Building, Loader2, Search, X } from 'lucide-react';
import { useEffect, useState } from 'react';

import { supabase } from '../../lib/supabase';

interface AccountHit {
  id: string;
  name: string;
  city: string | null;
}

/** Wildcards of a LIKE pattern, escaped: the search finds what is typed, literally. */
const likeSafe = (q: string) => q.replace(/[\\%_]/g, (c) => `\\${c}`);

/**
 * « Client (facultatif) » of a new mission (mock-up « Nouvelle mission »): a chip that opens a
 * search on the CRM accounts and keeps the one Gilles picks — by its id: the mission is tied to
 * the account he chose, never to a name matched afterwards (principle 12).
 */
export function AccountPickerButton({
  value,
  onChange,
  label = 'Client (facultatif)',
  exclude = [],
  placement = 'below',
}: {
  value: { id: string; name: string } | null;
  onChange: (next: { id: string; name: string } | null) => void;
  /** The chip's words before a pick (the events' relay picker says « Choisir le compte »). */
  label?: string;
  /** Accounts not to offer (a contact's sheet: those it is already linked to). */
  exclude?: readonly string[];
  /** `above` when the chip sits at the foot of a scrolling panel: the results open in view. */
  placement?: 'below' | 'above';
}) {
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<AccountHit[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setHits([]);
      return;
    }
    let alive = true;
    setLoading(true);
    const timer = window.setTimeout(async () => {
      const { data } = await supabase
        .from('crm_accounts')
        .select('id, name, city')
        .ilike('name', `%${likeSafe(q)}%`)
        .order('name')
        .limit(8);
      if (!alive) return;
      setHits((data ?? []).map((a) => ({ id: a.id, name: a.name ?? '(sans nom)', city: a.city ?? null })));
      setLoading(false);
    }, 200);
    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  }, [query]);

  if (value) {
    return (
      <span className="inline-flex h-8 items-center gap-1.5 rounded-full bg-desk-subtle pl-3 pr-1 text-sm font-medium text-desk-ink ring-1 ring-inset ring-desk-ring">
        <Building size={14} aria-hidden="true" />
        {value.name}
        <button
          type="button"
          onClick={() => onChange(null)}
          aria-label={`Retirer le client ${value.name}`}
          className="flex h-6 w-6 items-center justify-center rounded-full hover:bg-desk-hover"
        >
          <X size={13} />
        </button>
      </span>
    );
  }

  // The accounts already linked are left out — and said so when that leaves none.
  const shown = hits.filter((hit) => !exclude.includes(hit.id));
  return (
    <Popover className="relative">
      <Popover.Button className="inline-flex h-8 items-center gap-[7px] rounded-full bg-desk-subtle px-[11px] text-sm font-medium text-desk-strong ring-1 ring-inset ring-desk-ring transition hover:bg-desk-line focus:outline-none focus-visible:ring-2 focus-visible:ring-desk-focus">
        <Building size={14} strokeWidth={1.75} aria-hidden="true" />
        {label}
      </Popover.Button>
      <Popover.Panel
        className={`absolute left-0 z-30 w-80 rounded-xl bg-desk-card p-2 shadow-pop ring-1 ring-desk-ring ${placement === 'above' ? 'bottom-full mb-2' : 'mt-2'}`}
      >
        {({ close }) => (
          <>
            <label className="flex h-9 items-center gap-2 rounded-lg border border-desk-line px-2.5 text-desk-muted focus-within:border-desk-focus">
              <Search size={14} aria-hidden="true" />
              <span className="sr-only">Chercher un client</span>
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Chercher un client…"
                className="min-w-0 flex-1 border-0 bg-transparent p-0 text-sm text-desk-ink focus:outline-none focus:ring-0"
              />
              {loading && <Loader2 size={13} className="animate-spin" />}
            </label>
            <ul className="mt-1.5 max-h-64 overflow-y-auto">
              {query.trim().length >= 2 && !loading && shown.length === 0 && (
                <li className="px-2.5 py-2 text-sm text-desk-muted">
                  {hits.length === 0 ? 'Aucun compte ne porte ce nom.' : 'Déjà rattaché : aucun autre compte ne porte ce nom.'}
                </li>
              )}
              {shown.map((hit) => (
                <li key={hit.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onChange({ id: hit.id, name: hit.name });
                      setQuery('');
                      close();
                    }}
                    className="flex w-full items-baseline gap-2 rounded-lg px-2.5 py-2 text-left text-sm hover:bg-desk-hover"
                  >
                    <span className="truncate font-medium text-desk-ink">{hit.name}</span>
                    {hit.city && <span className="shrink-0 text-xs text-desk-muted">{hit.city}</span>}
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </Popover.Panel>
    </Popover>
  );
}
